// Nordicsell: minsta gemensamma prefix (antal tecken) som krävs för att ett ord
// ska räknas som "samma stam" i steg 3 av relevansfiltret (se nsRelevant).
// Höj värdet om steg 3 ger för breda träffar, sänk om böjningar missas.
const NS_STEM_MIN_PREFIX = 4;
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 120));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.resultsTarget = this.querySelector('[id^="PredictiveSearchResults-"]');
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.isHeaderSearch = this.closest('header') !== null;
    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });
    this.states = { OPEN: 'predictive-search-open', SEARCH_OPEN: 'search-open' };
    this._reqToken = 0;
    this._lastTitles = [];
    this.searchForm = this.querySelector('form');
    if (this.searchForm) this.searchForm.addEventListener('submit', this.onSubmit.bind(this));
  }
  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  onFocus() {
    document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
    document.documentElement.style.setProperty('--header-bottom-position', parseInt(this.header.getBoundingClientRect().bottom) + 'px');
    this.classList.add(this.states.OPEN);
    this.renderSuggestions(this.getQuery());
  }
  getQuery() { return this.input.value.trim(); }
  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.hideContent();
  }
  onChange() { this.renderSuggestions(this.getQuery()); }
  // Nordicsell: Enter i sökrutan ska ge exakt samma träffar som rullgardinen.
  // Shopifys sökresultatsida matchar bara HELA ord, så "kl" eller "sommark"
  // ger 0 träffar utan jokertecken. buildSearchUrl lägger därför till * på
  // sista ordet när det ser ut att vara ett påbörjat (ofullständigt) ord.
  onSubmit(event) {
    const value = this.getQuery();
    if (!value) return;
    event.preventDefault();
    window.location.href = this.buildSearchUrl(value, this._lastTitles);
  }
  isPartialToken(token, titles) {
    if (!token) return false;
    if (!titles || !titles.length) return token.length < 4;
    const safe = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('(^|[^0-9a-zA-ZÀ-ÿ])' + safe + '([^0-9a-zA-ZÀ-ÿ]|$)', 'i');
    return !titles.some((t) => re.test(t || ''));
  }
  buildSearchUrl(raw, titles) {
    const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '/search';
    const last = parts[parts.length - 1];
    if (!/[*"]$/.test(last) && this.isPartialToken(last.toLowerCase(), titles)) {
      parts[parts.length - 1] = last + '*';
    }
    return '/search?type=product&options%5Bprefix%5D=last&options%5Bfields%5D=title%2Cproduct_type%2Ctag%2Cvendor&q=' + encodeURIComponent(parts.join(' '));
  }
  thumbUrl(url) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=120';
  }
  formatMoney(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    const format = (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat) || '{{amount}}';
    const decimals = /no_decimals/.test(format) ? 0 : 2;
    let amount;
    try {
      amount = num.toLocaleString('sv-SE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    } catch (e) {
      amount = num.toFixed(decimals);
    }
    return format.replace(/\{\{\s*[a-z_]+\s*\}\}/i, amount);
  }
  // Nordicsell: relevansfilter för förslagen.
  // Shopifys /search/suggest.json viker bort diakritiska tecken, så "hals"
  // matchar även "Hälsa". Vi filtrerar därför svaret på klienten:
  // finns det träffar UTAN att vika bort prickarna visas BARA de; annars
  // faller vi tillbaka på de vikta träffarna (så att "halsa" utan prickar
  // ändå hittar "Hälsa"). Träffar som varken matchar exakt eller vikt tas
  // bort helt. Flerordssök: sista ordet prefix-testas, tidigare ord måste
  // finnas någonstans i texten (vikt jämförelse räcker där).
  nsFold(text) {
    return String(text == null ? '' : text).normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  nsWords(text) {
    return String(text == null ? '' : text)
      .toLowerCase()
      .split(/[^0-9a-zA-ZÀ-ÖØ-öø-ÿ]+/)
      .filter(Boolean);
  }
  nsMatches(text, tokens, fold) {
    const words = this.nsWords(text);
    if (!words.length || !tokens.length) return false;
    const last = tokens[tokens.length - 1];
    const needle = fold ? this.nsFold(last) : last;
    const hayWords = fold ? words.map((w) => this.nsFold(w)) : words;
    if (!hayWords.some((w) => w.indexOf(needle) === 0)) return false;
    if (tokens.length > 1) {
      const folded = ' ' + words.map((w) => this.nsFold(w)).join(' ') + ' ';
      for (let i = 0; i < tokens.length - 1; i++) {
        if (folded.indexOf(this.nsFold(tokens[i])) === -1) return false;
      }
    }
    return true;
  }
  // Nordicsell: antal inledande tecken som är lika i a och b.
  nsCommonPrefix(a, b) {
    const s1 = String(a == null ? '' : a);
    const s2 = String(b == null ? '' : b);
    const max = Math.min(s1.length, s2.length);
    let i = 0;
    while (i < max && s1.charAt(i) === s2.charAt(i)) i++;
    return i;
  }
  // Nordicsell: stam-matchning (steg 3). Som nsMatches, men sista ordet
  // behöver inte vara ett prefix - det räcker att det delar minst
  // NS_STEM_MIN_PREFIX inledande tecken med något ord i texten. Det gör att
  // svenska böjningar hittar varandra ("väska" -> "Väskor").
  nsStemMatches(text, tokens, fold) {
    const words = this.nsWords(text);
    if (!words.length || !tokens.length) return false;
    const last = tokens[tokens.length - 1];
    if (!last || last.length < NS_STEM_MIN_PREFIX) return false;
    const needle = fold ? this.nsFold(last) : last;
    const hayWords = fold ? words.map((w) => this.nsFold(w)) : words;
    if (!hayWords.some((w) => this.nsCommonPrefix(w, needle) >= NS_STEM_MIN_PREFIX)) return false;
    if (tokens.length > 1) {
      const folded = ' ' + words.map((w) => this.nsFold(w)).join(' ') + ' ';
      for (let i = 0; i < tokens.length - 1; i++) {
        if (folded.indexOf(this.nsFold(tokens[i])) === -1) return false;
      }
    }
    return true;
  }
  nsRelevant(items, getText, raw) {
    const list = items || [];
    const tokens = this.nsWords(raw);
    if (!tokens.length || !list.length) return list;
    const exact = list.filter((item) => this.nsMatches(getText(item), tokens, false));
    if (exact.length) return exact;
    const folded = list.filter((item) => this.nsMatches(getText(item), tokens, true));
    if (folded.length) return folded;
    // Steg 3 (endast när steg 1 och 2 båda är tomma): stam-matchning för
    // svenska böjningar. Korta sökord (< NS_STEM_MIN_PREFIX tecken) får
    // aldrig nå hit - "kl" och "sko" skulle då matcha alldeles för brett.
    const lastToken = tokens[tokens.length - 1];
    if (!lastToken || lastToken.length < NS_STEM_MIN_PREFIX) return folded;
    const stem = list.filter((item) => this.nsStemMatches(getText(item), tokens, false));
    if (stem.length) return stem;
    return list.filter((item) => this.nsStemMatches(getText(item), tokens, true));
  }
  // Nordicsell: strukturerad sökruta i Zalando-stil.
  // Ordning: Kategorier (matchande kollektioner, med kurerade termer som
  // reserv) -> Produkter (bild, titel, pris) -> "Visa alla resultat för ...".
  // Sökningen är fältbegränsad till title, product_type, tag och vendor så att
  // produktbeskrivningar aldrig styr träffarna.
  async renderSuggestions(query) {
    if (!this.resultsTarget) return;
    const raw = (query || '').trim();
    const q = raw.toLowerCase();
    if (q.length === 0) { this.hideContent(); return; }
    const token = ++this._reqToken;
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const icon = `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'><path d='M7.25 12.5C10.1495 12.5 12.5 10.1495 12.5 7.25C12.5 4.35051 10.1495 2 7.25 2C4.35051 2 2 4.35051 2 7.25C2 10.1495 4.35051 12.5 7.25 12.5Z' stroke='currentColor' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/><path d='M10.9609 10.9636L13.9985 14.0012' stroke='currentColor' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/></svg>`;
    const labels = window.nsSuggestLabels || {};
    const labelCategories = labels.categories || 'Kategorier';
    const labelProducts = labels.products || 'Produkter';
    const labelAll = labels.all || 'Visa alla resultat för';
    const terms = window.nsSuggestTerms || [];
    const starts = [], words = [];
    terms.forEach((t) => {
      const tl = t.toLowerCase();
      if (tl.startsWith(q)) starts.push(t);
      else { const parts = tl.split(/[\s&–—/-]+/); if (parts.some((w) => w.startsWith(q))) words.push(t); }
    });
    const termList = starts.concat(words).slice(0, 5);
    const highlight = (t) => {
      const tl = t.toLowerCase();
      const idx = tl.indexOf(q);
      if (idx === -1) return esc(t);
      return esc(t.slice(0, idx)) + '<mark>' + esc(t.slice(idx, idx + q.length)) + '</mark>' + esc(t.slice(idx + q.length));
    };
    const heading = (text) => `<div class='ns-suggest__heading'>${esc(text)}</div>`;
    const build = (collections, products, titles) => {
      if (token !== this._reqToken) return;
      const allHref = this.buildSearchUrl(raw, titles);
      let html = '';
      const cats = (collections && collections.length)
        ? collections.map((c) => ({ title: c.title, url: c.url }))
        : termList.map((t) => ({ title: t, url: this.buildSearchUrl(t, [t]) }));
      if (cats.length) {
        html += heading(labelCategories);
        html += `<ul class='ns-suggest' role='group'>${cats.map((c) => `<li role='option' aria-selected='false'><a href='${esc(c.url)}'>${icon}<span>${highlight(c.title)}</span></a></li>`).join('')}</ul>`;
      }
      if (products && products.length) {
        html += heading(labelProducts);
        html += `<ul class='ns-suggest ns-suggest--products' role='group'>${products.map((p) => {
          const img = this.thumbUrl((p.featured_image && p.featured_image.url) || p.image || '');
          const imgHtml = img ? `<img src='${esc(img)}' alt='' width='44' height='44' loading='eager' decoding='async'>` : '';
          const price = this.formatMoney(p.price);
          const priceHtml = price ? `<span class='ns-suggest__price'>${esc(price)}</span>` : '';
          return `<li role='option' aria-selected='false'><a href='${esc(p.url)}'>${imgHtml}<span class='ns-suggest__title'>${esc(p.title)}</span>${priceHtml}</a></li>`;
        }).join('')}</ul>`;
      }
      html += `<a class='ns-suggest__all' href='${esc(allHref)}' style='display:flex;align-items:center;gap:8px;padding:10px 12px;font-weight:600'>${icon}<span>${esc(labelAll)} «${esc(raw)}»</span></a>`;
      this.resultsTarget.innerHTML = html;
      this.showContent();
    };
    build(null, null, this._lastTitles);
    try {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection'
        + '&resources[limit]=10'
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=title,product_type,tag,vendor';
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const results = (data.resources && data.resources.results) || {};
      // Nordicsell punkt 169 (2026-10-25): dolda produkter och kategorier visas aldrig
      // (regeln i snippets/ns-sokfilter.liquid, window.nsSok).
      if (window.nsSok) {
        results.products = (results.products || []).filter((p) => window.nsSok.synlig(p));
        results.collections = (results.collections || []).filter((c) => window.nsSok.kategoriOk(c));
      }
      // OBS: /search/suggest.json returnerar produkttypen som `type` (inte `product_type`).
      const productText = (p) => [p && p.title, p && (p.product_type || p.type), p && p.vendor].filter(Boolean).join(' ');
      const matchedProducts = this.nsRelevant(results.products || [], productText, raw);
      const products = matchedProducts.slice(0, 6);
      const seen = {};
      const uniqueCollections = (results.collections || []).filter((c) => {
        if (!c || !c.title) return false;
        if (/^annonser/i.test(c.title)) return false;
        const key = c.title.toLowerCase();
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
      const collections = this.nsRelevant(uniqueCollections, (c) => c.title, raw).slice(0, 4);
      const titles = collections.map((c) => c.title).concat(matchedProducts.map((p) => p.title));
      if (token === this._reqToken) this._lastTitles = titles;
      build(collections, products, titles);
    } catch (e) {}
  }
  showContent() {
    this.searchContent?.classList.remove('hidden');
    this.classList.add(this.states.OPEN);
    if (this.isHeaderSearch && !document.body.classList.contains(this.states.SEARCH_OPEN)) {
      document.body.classList.add(this.states.SEARCH_OPEN);
      this.toggleNavButtonVisibility(true);
    }
    this.setAttribute('results', 'true');
  }
  hideContent() {
    if (this.resultsTarget) this.resultsTarget.innerHTML = '';
    this.searchContent?.classList.add('hidden');
  }
  handleClickOutside(event) {
    const target = event.target;
    if (this.contains(target)) return;
    if (document.activeElement === this.input) return;
    const shouldClose = this.isHeaderSearch ? (target.classList.contains('fixed-overlay') && target.closest('.header-section')) || target.classList.contains('header__search-close') : true;
    if (shouldClose) { setTimeout(() => this.toggleSearchState(false)); }
  }
  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen) { this.searchContent?.classList.add('hidden'); }
  }
  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }
}
customElements.define('predictive-search', PredictiveSearch);
